import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { loginRedirect } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Coins, Wallet as WalletIcon, ArrowUpFromLine, Send, RefreshCw, TrendingUp, ArrowLeft, Fingerprint } from "lucide-react";
import { parseAuthOptions, credentialToJSON, passkeysSupported } from "@/lib/webauthn";

// One page covers: opt-in preferences, holdings dashboard, withdraw (WUID-gated) + friend transfer.
export default function WalletHoldings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const [prefs, setPrefs] = useState(null);
  const [holdings, setHoldings] = useState(null);
  const [balance, setBalance] = useState(null);
  const [busy, setBusy] = useState(false);
  const [wuidMe, setWuidMe] = useState(null);

  const reload = async () => {
    if (!user) return;
    try {
      const [p, h, b, w] = await Promise.all([
        api.get("/wallet/auto-convert/preferences"),
        api.get("/wallet/holdings"),
        api.get("/wallet"),
        api.get("/wuid/me").catch(() => ({ data: null })),
      ]);
      setPrefs(p.data); setHoldings(h.data); setBalance(b.data); setWuidMe(w.data);
    } catch (e) {
      console.error(e);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [user]);

  // Voice intent hand-off from the global mic (see VoiceSearchMic.jsx).
  // If the user said something like "convert to gold" or "withdraw 200", we scroll
  // the relevant card into view and prompt them to confirm — never auto-execute.
  useEffect(() => {
    const intent = routerLocation.state?.voice_intent;
    if (!intent) return;
    const scrollTo = (id) => {
      const el = document.querySelector(`[data-voice-target="${id}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    if (intent.action === "convert") {
      setTimeout(() => scrollTo("convert"), 400);
      toast.info("Tap 'Convert Now' below to move idle wallet cash to 50/50 gold + silver.");
    } else if (intent.action === "withdraw") {
      setTimeout(() => scrollTo("withdraw"), 400);
      toast.info(intent.amount
        ? `Ready to withdraw ₹${intent.amount} — please confirm below.`
        : "Withdraw form is below — WUID identity required.");
    }
    // Clear router state so a refresh doesn't re-trigger the intent.
    navigate(routerLocation.pathname, { replace: true, state: null });
  }, [routerLocation.state]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-5 py-16 text-center">
        <h2 className="heading text-3xl font-extrabold mb-3">Please login</h2>
        <Button onClick={loginRedirect} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="wallet-holdings-login">Login with Google</Button>
      </div>
    );
  }

  if (!prefs || !holdings || !balance) return <div className="max-w-3xl mx-auto px-4 py-8 text-stone-500">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6" data-testid="wallet-holdings-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="wallet-back"><ArrowLeft size={16} className="mr-1" /> Back</Button>
        <h1 className="heading text-2xl font-extrabold">Wallet · Gold & Silver</h1>
      </div>

      <BalanceCard balance={balance} onRefresh={reload} />
      <PrefsCard prefs={prefs} balance={balance} onSaved={reload} />
      <HoldingsCard holdings={holdings} />
      <WithdrawCard wuidMe={wuidMe} balance={balance} onDone={reload} />
    </div>
  );
}


function BalanceCard({ balance, onRefresh }) {
  const rupees = ((balance?.balance_paise || 0) / 100).toFixed(2);
  const free = ((balance?.free_credit_paise || 0) / 100).toFixed(2);
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2"><WalletIcon size={18} /> Wallet balance</CardTitle></CardHeader>
      <CardContent className="flex items-baseline justify-between">
        <div>
          <div className="text-3xl font-black tracking-tight" data-testid="wallet-balance">₹{rupees}</div>
          <div className="text-xs text-stone-500 mt-1">+ ₹{free} free credit</div>
        </div>
        <Button size="sm" variant="outline" onClick={onRefresh} className="rounded-full gap-2" data-testid="wallet-refresh"><RefreshCw size={14} /> Refresh</Button>
      </CardContent>
    </Card>
  );
}


function PrefsCard({ prefs, balance, onSaved }) {
  const [enabled, setEnabled] = useState(prefs.enabled);
  const [idleDays, setIdleDays] = useState(String(prefs.idle_days || 60));
  const [threshold, setThreshold] = useState(String(prefs.threshold_paise || 100000));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/wallet/auto-convert/preferences", {
        enabled, idle_days: parseInt(idleDays), threshold_paise: parseInt(threshold),
      });
      toast.success("Preferences saved");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const runNow = async () => {
    if (!window.confirm(`Convert ₹${(balance.balance_paise / 100).toFixed(2)} to 50% Gold + 50% Silver now?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/wallet/auto-convert/run");
      toast.success(`Converted — ${data.gold_g}g gold + ${data.silver_g}g silver at live spot`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Convert failed");
    } finally { setBusy(false); }
  };

  return (
    <Card data-voice-target="convert">
      <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2"><Coins size={18} /> Auto-convert to Gold & Silver</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-stone-600">Idle wallet earnings automatically convert to <b>50% gold + 50% silver</b> holdings — earning <b>2% annual interest</b> after {holdingsMinHold(prefs)} days. Any time, you can convert back or withdraw.</div>
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold">Enable auto-convert</span>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="autoconvert-toggle" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-widest text-stone-500">Idle threshold (days)</span>
            <Select value={idleDays} onValueChange={setIdleDays}>
              <SelectTrigger className="mt-1.5" data-testid="autoconvert-idle-days"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="60">60 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-widest text-stone-500">Min balance (₹)</span>
            <Input type="number" value={(parseInt(threshold) / 100).toString()}
              onChange={(e) => setThreshold(String(Math.max(100, parseInt(e.target.value || 0)) * 100))}
              className="mt-1.5" data-testid="autoconvert-threshold" />
          </label>
        </div>
        <div className="flex gap-2">
          <Button onClick={save} disabled={busy} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="autoconvert-save">
            {busy ? "Saving…" : "Save"}
          </Button>
          <Button variant="outline" onClick={runNow} disabled={busy || !enabled || balance.balance_paise < prefs.threshold_paise} className="rounded-full gap-2" data-testid="autoconvert-run-now">
            <Coins size={14} /> Convert now
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


function HoldingsCard({ holdings }) {
  const t = holdings.totals || {};
  const rows = holdings.by_metal || [];
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2"><TrendingUp size={18} /> Metal holdings</CardTitle></CardHeader>
      <CardContent className="space-y-3" data-testid="metal-holdings">
        <div className="grid grid-cols-3 gap-3 text-center">
          <Stat label="Invested" value={`₹${t.invested_inr?.toFixed?.(2) ?? "0.00"}`} />
          <Stat label="Current value" value={`₹${t.current_value_inr?.toFixed?.(2) ?? "0.00"}`} />
          <Stat label="Interest (2%/yr)" value={`₹${t.accrued_interest_inr?.toFixed?.(2) ?? "0.00"}`} tone="emerald" />
        </div>
        {rows.length === 0 ? (
          <div className="text-sm text-stone-500 text-center py-4">No metal holdings yet — enable auto-convert or click <b>Convert now</b>.</div>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.metal} className="flex items-center justify-between p-3 rounded-lg bg-stone-50" data-testid={`holding-${r.metal}`}>
                <div>
                  <div className="font-semibold capitalize">{r.metal}</div>
                  <div className="text-xs text-stone-500">{r.quantity_g} g · {r.lots} lot(s)</div>
                </div>
                <div className="text-right">
                  <div className="font-semibold">₹{r.current_value_inr.toFixed(2)}</div>
                  <div className="text-xs text-emerald-700">+₹{r.accrued_interest_inr.toFixed(2)} interest</div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="text-xs text-stone-500 text-right">Prices: gold ₹{holdings.prices?.gold}/g · silver ₹{holdings.prices?.silver}/g · source: {holdings.prices?.source}</div>
      </CardContent>
    </Card>
  );
}


function WithdrawCard({ wuidMe, balance, onDone }) {
  const [dest, setDest] = useState("bank");
  const [rupees, setRupees] = useState("");
  const [bank, setBank] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [upi, setUpi] = useState("");
  const [friendWuid, setFriendWuid] = useState("");
  const [busy, setBusy] = useState(false);

  const hasWuid = !!wuidMe;

  const stepUpPasskey = async () => {
    if (!passkeysSupported()) throw new Error("Your browser does not support Passkeys");
    const { data: opts } = await api.post("/wuid/webauthn/auth/begin");
    const publicKey = parseAuthOptions(opts);
    const assertion = await navigator.credentials.get({ publicKey });
    await api.post("/wuid/webauthn/auth/complete", credentialToJSON(assertion));
  };

  const submit = async () => {
    const amount_paise = Math.round(parseFloat(rupees || "0") * 100);
    if (amount_paise < 10000) return toast.error("Minimum withdrawal is ₹100");
    if (amount_paise > (balance.balance_paise || 0)) return toast.error("Insufficient balance");
    setBusy(true);
    try {
      try { await stepUpPasskey(); }
      catch (e) { throw new Error("Passkey verification failed — " + (e.message || "please try again")); }

      const body = { amount_paise, destination: dest };
      if (dest === "bank") { body.bank_account = bank; body.ifsc = ifsc.toUpperCase(); }
      if (dest === "upi") body.upi_id = upi;
      if (dest === "friend") body.friend_wuid = friendWuid;

      const { data } = await api.post("/wallet/withdraw", body);
      toast.success(dest === "friend" ? "Sent to friend!" : "Withdrawal queued — 1-2 business days");
      setRupees(""); setBank(""); setIfsc(""); setUpi(""); setFriendWuid("");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Withdraw failed");
    } finally { setBusy(false); }
  };

  return (
    <Card data-voice-target="withdraw">
      <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2"><ArrowUpFromLine size={18} /> Withdraw</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {!hasWuid && (
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900" data-testid="withdraw-need-wuid">
            <b>WUID required.</b> Withdrawals need a verified World Unique ID.
            <Button size="sm" variant="link" className="ml-2 px-0" onClick={() => window.location.assign("/wuid")} data-testid="withdraw-goto-wuid">Set up WUID →</Button>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <label>
            <span className="text-xs font-bold uppercase tracking-widest text-stone-500">Destination</span>
            <Select value={dest} onValueChange={setDest}>
              <SelectTrigger className="mt-1.5" data-testid="withdraw-dest"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bank">Bank account</SelectItem>
                <SelectItem value="upi">UPI</SelectItem>
                <SelectItem value="friend">Find2Near friend (WUID)</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <label>
            <span className="text-xs font-bold uppercase tracking-widest text-stone-500">Amount (₹)</span>
            <Input type="number" value={rupees} onChange={(e) => setRupees(e.target.value)} placeholder="100" className="mt-1.5" data-testid="withdraw-amount" />
          </label>
        </div>
        {dest === "bank" && (
          <div className="grid grid-cols-2 gap-3">
            <Input value={bank} onChange={(e) => setBank(e.target.value)} placeholder="Account number" data-testid="withdraw-bank-account" />
            <Input value={ifsc} onChange={(e) => setIfsc(e.target.value.toUpperCase())} placeholder="IFSC / SWIFT" data-testid="withdraw-ifsc" />
          </div>
        )}
        {dest === "upi" && (
          <Input value={upi} onChange={(e) => setUpi(e.target.value)} placeholder="you@upi" data-testid="withdraw-upi" />
        )}
        {dest === "friend" && (
          <Input value={friendWuid} onChange={(e) => setFriendWuid(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="Friend's 10-digit WUID" inputMode="numeric" maxLength={10} data-testid="withdraw-friend-wuid" />
        )}
        <Button disabled={busy || !hasWuid || !rupees} onClick={submit} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a] gap-2" data-testid="withdraw-submit">
          <Fingerprint size={14} /> {busy ? "Verifying…" : "Authorise with Passkey & withdraw"}
        </Button>
        <div className="text-xs text-stone-500">Passkey re-authentication protects every withdrawal — even if your session is hijacked.</div>
      </CardContent>
    </Card>
  );
}


function Stat({ label, value, tone = "stone" }) {
  const colorMap = { emerald: "text-emerald-700", stone: "text-stone-900" };
  return (
    <div className="p-3 rounded-lg bg-white border border-stone-200">
      <div className={`text-lg font-black ${colorMap[tone]}`}>{value}</div>
      <div className="text-xs text-stone-500 mt-0.5">{label}</div>
    </div>
  );
}

function holdingsMinHold() { return 90; }  // shown copy — actual server rule enforced by METAL_MIN_HOLD_DAYS
